import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * The "2 shorts for ₹1,199" add-on in the cart. Always ends with ONE combo
 * line in the cart (one item, ₹1,199). Any separate shorts being folded in
 * are removed only after the combo is safely added, so a failure never
 * leaves the customer with fewer shorts than they chose. Then the cart
 * re-renders itself through the same lines-update event the product form
 * uses, which also redraws the offer bar and this add-on.
 */
class ShortsOffer extends HTMLElement {
  /**
   * Read fresh every time, never cached. The cart re-renders by morphing, so
   * this element survives a cart change with new markup inside it - a copy
   * taken on connect went stale (it still thought the cart had no shorts)
   * and every add after the first failed as "sold out".
   */
  get data() {
    try {
      return JSON.parse(this.querySelector('[data-shorts-data]').textContent);
    } catch {
      return { combo: {}, shorts: {}, loose: [] };
    }
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#sync);
    this.#sync();
    // Markup swapped in by a morph needs its sold-out sizes marked again.
    this.observer = new MutationObserver(() => this.#sync());
    this.observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#sync);
    this.observer?.disconnect();
  }

  #onClick = (event) => {
    const dialog = this.querySelector('[data-details-dialog]');
    if (event.target.closest('[data-details]')) {
      // Open on the colour of the first short in the pair.
      const first = this.querySelector('[data-pick]');
      this.#showDetailsTab(first && this.#colourOf(first) === 'Olive' ? 'olive' : 'black');
      dialog?.showModal();
      return;
    }
    if (dialog?.open) {
      const tab = event.target.closest('[data-details-tab]');
      if (tab) this.#showDetailsTab(tab.dataset.detailsTab);
      // The close button, or a tap on the backdrop. A tap on the dialog's own
      // padding also targets the dialog, so check it really fell outside.
      const r = dialog.getBoundingClientRect();
      const outside = event.target === dialog &&
        (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom);
      if (event.target.closest('[data-details-close]') || outside) dialog.close();
      return;
    }
    const swatch = event.target.closest('[data-colour]');
    if (swatch && !swatch.disabled) {
      swatch.parentElement.querySelectorAll('[data-colour]').forEach((b) => b.setAttribute('aria-pressed', String(b === swatch)));
      this.#sync();
      return;
    }
    if (event.target.closest('[data-add]')) this.#add();
  };

  #showDetailsTab(key) {
    this.querySelectorAll('[data-details-tab]').forEach((t) => t.setAttribute('aria-selected', String(t.dataset.detailsTab === key)));
    this.querySelectorAll('[data-details-panel]').forEach((p) => { p.hidden = p.dataset.detailsPanel !== key; });
  }

  #colourOf(pick) {
    return pick.querySelector('[aria-pressed="true"]')?.dataset.colour === 'olive' ? 'Olive' : 'Black';
  }

  /** Disable sizes that are sold out in the chosen colour; follow the image. */
  #sync = () => {
    const { shorts, loose } = this.data;
    // The pair in order: shorts already in the cart first, then the pickers -
    // the same order #plan() builds the combo in.
    const colours = loose.slice(0, 2).map((u) => (u.short.startsWith('Olive') ? 'Olive' : 'Black'));
    for (const pick of this.querySelectorAll('[data-pick]')) {
      const colour = this.#colourOf(pick);
      colours.push(colour);
      const select = pick.querySelector('[data-size]');
      for (const option of select.options) {
        option.disabled = !shorts[`${colour} / ${option.value}`];
        // Only write when it differs: the observer watching for morphs would
        // otherwise see this write and call #sync forever.
        const label = (option.dataset.label || option.value) + (option.disabled ? ' – sold out' : '');
        if (option.textContent !== label) option.textContent = label;
      }
      if (select.selectedOptions[0]?.disabled) {
        const next = [...select.options].find((o) => !o.disabled);
        if (next) select.value = next.value;
      }
    }
    this.querySelectorAll('[data-shorts-image]').forEach((img, i) => {
      const src = img.dataset[colours[i] === 'Olive' ? 'srcOlive' : 'srcBlack'];
      if (src && img.getAttribute('src') !== src) img.src = src;
    });
  };

  /** The two shorts making up the combo, and the loose cart units they replace. */
  #plan() {
    const picked = [...this.querySelectorAll('[data-pick]')].map(
      (pick) => `${this.#colourOf(pick)} / ${pick.querySelector('[data-size]').value}`
    );
    const loose = this.data.loose.slice(0, 2 - picked.length);
    const shorts = [...loose.map((u) => u.short), ...picked];
    const variant = this.data.combo[`${shorts[0]}|${shorts[1]}`];
    const removeBy = {};
    for (const u of loose) removeBy[u.key] = (removeBy[u.key] || 0) + 1;
    return { variant, removeBy };
  }

  async #fetchJson(url, body) {
    const r = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    return r.json();
  }

  async #add() {
    const button = this.querySelector('[data-add]');
    const error = this.querySelector('[data-error]');
    error.hidden = true;
    const { variant, removeBy } = this.#plan();
    if (!variant || !variant.available) {
      error.textContent = 'That combination is sold out - pick another size or colour.';
      error.hidden = false;
      return;
    }

    button.setAttribute('aria-busy', 'true');
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((c) => c.dataset.sectionId)
      .filter(Boolean)
      .join(',');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const hasRemovals = Object.keys(removeBy).length > 0;
      let response = await this.#fetchJson(Theme.routes.cart_add_url, {
        items: [{ id: variant.id, quantity: 1 }],
        ...(hasRemovals ? {} : { sections }),
      });
      if (response.status) throw new Error(response.description || response.message || 'Could not add the shorts.');

      if (hasRemovals) {
        const cartNow = await this.#fetchJson(`${Theme.routes.cart_url}.js`);
        const updates = {};
        for (const [key, n] of Object.entries(removeBy)) {
          const line = cartNow.items.find((i) => i.key === key);
          if (line) updates[key] = Math.max(0, line.quantity - n);
        }
        response = await this.#fetchJson(`${Theme.routes.cart_url}/update.js`, { updates, sections });
      }

      const cart = await this.#fetchJson(`${Theme.routes.cart_url}.js`);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'shorts-offer', itemCount: 1, sections: response.sections, didError: false },
      });
    } catch (e) {
      const cart = await this.#fetchJson(`${Theme.routes.cart_url}.js`).catch(() => null);
      if (cart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'shorts-offer', didError: true },
        });
      } else {
        deferred.reject(e);
      }
      error.textContent = e.message || 'Could not add the shorts - please try again.';
      error.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('shorts-offer')) customElements.define('shorts-offer', ShortsOffer);
